'use client';

import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { AVATAR_VIDEO_PREVIEWS, SHOP_ITEMS_DATA } from '@/lib/shop-data';
import { cn, resolveAvatarUrl, getAvatarOverrideClasses } from '@/lib/utils';
import type { Player } from '@/lib/types';

interface PlayerAvatarProps {
  player: Player;
  className?: string;
  avatarClassName?: string;
  showMuteButton?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  animateVideo?: boolean;
}

export function PlayerAvatar({ player, className, avatarClassName, size = 'md', animateVideo = false }: PlayerAvatarProps) {
  // Game modules look players up by uid from state that can outlive the lobby
  // roster (a submission, a vote, a scoreboard row). A missing player must not
  // take the whole game module down with a TypeError.
  if (!player) return null;

  const hasVideo = player.activeAvatarImage && AVATAR_VIDEO_PREVIEWS[player.activeAvatarImage];
  const videoUrl = hasVideo ? AVATAR_VIDEO_PREVIEWS[player.activeAvatarImage!] : null;

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-16 w-16',
    xl: 'h-24 w-24 sm:h-32 sm:w-32',
  };

  return (
    <div className={cn('relative group/avatar', className)}>
      <Avatar className={cn(
        sizeClasses[size],
        'border-2 border-slate-950/20 relative overflow-hidden transition-all duration-300',
        getAvatarOverrideClasses(player.activeAvatarFrame),
        avatarClassName
      )}>
        {hasVideo ? (
          <video
            src={videoUrl!}
            autoPlay={animateVideo}
            muted
            onCanPlay={(e) => {
              e.currentTarget.volume = 0;
              e.currentTarget.muted = true;
            }}
            loop={animateVideo}
            preload={animateVideo ? 'auto' : 'metadata'}
            playsInline
            className="h-full w-full object-cover"
          />
        ) : (
          <AvatarImage 
            src={resolveAvatarUrl(player, SHOP_ITEMS_DATA) || undefined} 
          />
        )}
        {!hasVideo && (
          <AvatarFallback className="font-black bg-slate-900 text-primary">
            {player.displayName?.charAt(0) || 'U'}
          </AvatarFallback>
        )}
      </Avatar>
    </div>
  );
}
